import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

interface PageTitle {
  title: string;
  subtitle?: ReactNode;
}

interface PageTitleStore {
  current: PageTitle | null;
  announce: (title: PageTitle | null) => void;
}

const Context = createContext<PageTitleStore | null>(null);

/**
 * Where the screen says what it is.
 *
 * <p>Every page used to open with a heading block: a title, a sentence under it, and a button
 * opposite. On a laptop that is the first eighty pixels of every screen spent saying something
 * the reader already knows, because they clicked the thing in the sidebar that brought them
 * here — and it pushed the table they came for below the fold.
 *
 * <p>So the title moves into the bar along the top, which was empty on its left half. The page
 * keeps announcing what it is; only where that announcement is drawn has changed.
 *
 * <p>A context rather than each page reaching into the layout: the header is rendered above the
 * router, so a page cannot write into it directly without the two being rendered in the wrong
 * order. This lets the page say what it is on mount and the header read it.
 */
export function PageTitleProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<PageTitle | null>(null);
  const store = useMemo<PageTitleStore>(
    () => ({ current, announce: setCurrent }),
    [current],
  );
  return <Context.Provider value={store}>{children}</Context.Provider>;
}

/** What the header should show, for the header itself to read. */
export function usePageTitle(): PageTitle | null {
  return useContext(Context)?.current ?? null;
}

/**
 * Announces what this screen is, for as long as it is on screen.
 *
 * <p>Cleared on the way out so a screen that forgets to announce itself shows nothing rather
 * than the last page's title, which would be worse than an empty space — it would be a lie.
 */
export function useAnnouncePage(title: string, subtitle?: ReactNode): void {
  const store = useContext(Context);
  const announce = store?.announce;

  useEffect(() => {
    if (!announce) {
      return;
    }
    announce({ title, subtitle });
    return () => announce(null);
  }, [announce, title, subtitle]);
}
